import {
  AlignLeft,
  Contact,
  CreditCard,
  ContactRound,
  FileText,
  Image,
  LayoutList,
  QrCode,
  Sigma,
  Table,
  Type,
  Users,
  type LucideIcon,
} from 'lucide-react';
import {
  PDF_BLOCK_TYPES,
  PDF_LETTERHEAD_BLOCK_TYPES,
  PDF_SINGLETON_BLOCK_TYPES,
  type PdfBlockType,
  type PdfLayoutBlock,
} from '@erp/shared';

export interface BlockDefinition {
  type: PdfBlockType;
  icon: LucideIcon;
  /** i18n-Schlüssel unterhalb von `pdfDesigner.blocks`. */
  labelKey: string;
  isSingleton: boolean;
  isLetterhead: boolean;
  /** Bausteine mit freiem Text bieten im Inspektor ein Textfeld an. */
  supportsContent: boolean;
}

const ICONS: Readonly<Record<PdfBlockType, LucideIcon>> = {
  LOGO: Image,
  SENDER_LINE: AlignLeft,
  RECIPIENT_ADDRESS: Users,
  DOCUMENT_META: LayoutList,
  HEADER_TEXT: Type,
  CUSTOMER_HEADER: ContactRound,
  RECEIVING_HOURS: LayoutList,
  TRANSPORT_PLAN: LayoutList,
  SHIPPING_SUMMARY: LayoutList,
  ITEMS_TABLE: Table,
  TOTALS_BLOCK: Sigma,
  GIROCODE_QR: QrCode,
  FOOTER_TEXT: CreditCard,
  FOOTER_COLUMNS: Contact,
  CUSTOM_HTML_TEXT: FileText,
};

const CONTENT_BLOCKS: readonly PdfBlockType[] = ['HEADER_TEXT', 'FOOTER_TEXT', 'CUSTOM_HTML_TEXT'];

export const BLOCK_DEFINITIONS: readonly BlockDefinition[] = PDF_BLOCK_TYPES.map((type) => ({
  type,
  icon: ICONS[type],
  labelKey: type,
  isSingleton: PDF_SINGLETON_BLOCK_TYPES.includes(type),
  isLetterhead: PDF_LETTERHEAD_BLOCK_TYPES.includes(type),
  supportsContent: CONTENT_BLOCKS.includes(type),
}));

export function blockDefinition(type: PdfBlockType): BlockDefinition {
  const found = BLOCK_DEFINITIONS.find((entry) => entry.type === type);
  if (found === undefined) {
    throw new Error(`Unbekannter Blocktyp: ${type}`);
  }
  return found;
}

/** Neue Bausteine landen unterhalb der bestehenden Fließtext-Blöcke. */
export function createBlock(type: PdfBlockType, blocks: readonly PdfLayoutBlock[]): PdfLayoutBlock {
  const definition = blockDefinition(type);
  const maxSortOrder = blocks.reduce((max, block) => Math.max(max, block.sortOrder), 0);
  const contentWidth = blocks.find((block) => !block.position.isAbsolute)?.position.width ?? 170;

  return {
    id: `${type.toLowerCase()}-${Date.now().toString(36)}`,
    type,
    sortOrder: maxSortOrder + 10,
    isVisible: true,
    pageBreakBefore: false,
    content: '',
    position: definition.isLetterhead
      ? { x: 20, y: 20, width: 80, height: 20, isAbsolute: true }
      : { x: 0, y: 0, width: contentWidth, height: 0, isAbsolute: false },
    styles: {
      paddingTop: 3,
      paddingBottom: 0,
      backgroundColor: null,
      borderBottom: false,
      textAlign: 'left',
      fontSize: 9,
      isBold: false,
    },
    options: {
      showTaxBreakdown: true,
      showCashDiscount: true,
      showPaymentTerms: true,
      showTaxNotice: true,
      qrSizeMm: 30,
      qrAlign: 'left',
      qrShowCaption: true,
      logoHeightMm: 14,
      showCompanyAddress: true,
      customerHeaderFieldKeys: [],
      customerHeaderColumns: 0,
      customerHeaderLabelMode: 'LAYOUT',
      customerHeaderHideEmpty: true,
    },
  };
}
